/* ==================================================================
   Big Billion Days · API layer
   Fetches the campaign payload and maps `resp.bigBillionDays` onto the
   model the page renders. Sections that are disabled, or not meant for
   this user's audience, come back as `null` so the page simply skips them.
   ================================================================== */
import * as APIHelper from '../Helper/apiFetch';
import { BBD_MOCK_RESPONSE } from './mockResponse';

// TODO: flip to false once the backend ships `bigBillionDays` in the topFive
// payload (see mockResponse.js for the proposed contract).
const USE_MOCK = true;

/* who a section is for, evaluated against `bigBillionDays.user` */
const AUDIENCE = {
  ALL: () => true,
  PHYSICAL_CARD_HOLDERS: (user) => user.physicalCardHolder === true,
  NON_PHYSICAL_CARD_HOLDERS: (user) => !user.physicalCardHolder,
};

/* the section object when this user should see it, otherwise null */
function forUser(section, user) {
  if (!section || section.enabled === false) return null;
  const allowed = AUDIENCE[section.audience] || AUDIENCE.ALL;
  return allowed(user) ? section : null;
}

/* The hero "Add money" offer is driven by the `firstCardLoad` milestone inside
   `welcomeMilestones.items` — we take only that one item and ignore the rest.
   Shown only when the section is `eligible` (NOT `enabled`), the audience matches,
   and the `firstCardLoad` milestone is NOT yet `completed`. Once it's completed
   (reward credited) the section is hidden. Returns the { text, highlight, cta }
   the OfferCard renders, or null otherwise. */
function addMoneyFromMilestones(wm, user) {
  if (!wm || !wm.eligible) return null;
  const allowed = AUDIENCE[wm.audience] || AUDIENCE.ALL;
  if (!allowed(user)) return null;
  const item = (wm.items || []).find((it) => it.key === 'firstCardLoad');
  if (!item || item.completed === true) return null;
  return {
    text: item.text,
    highlight: item.highlight,
    cta: item.cta,
    rewardAmount: item.rewardAmount,
  };
}

/* Deep-ish merge of the live `bigBillionDays` over the mock: live top-level
   fields and live section objects win (so flags like `enabled` are respected),
   while the mock fills in any section the backend hasn't shipped yet. */
function mergeBbd(mock, live) {
  if (!live) return mock;
  const out = { ...mock, ...live };
  ['welcomeMilestones', 'physicalCard', 'cardSpend', 'gold', 'vouchers', 'bills'].forEach((k) => {
    if (mock[k] || live[k]) out[k] = { ...(mock[k] || {}), ...(live[k] || {}) };
  });
  if (mock.user || live.user) out.user = { ...(mock.user || {}), ...(live.user || {}) };
  return out;
}

/* maps resp.bigBillionDays → page model, or null when the campaign is off */
export function mapBbd(resp) {
  const data = resp && (resp.data ?? resp);
  const bbd = data && data.bigBillionDays;
  if (!bbd || bbd.enabled === false) return null;

  const user = bbd.user || {};
  return {
    startsOn: bbd.campaignStart || null,
    endsOn: bbd.campaignEnd || null,
    banner: bbd.banner || null,
    user,
    addMoney: addMoneyFromMilestones(bbd.welcomeMilestones, user),
    physicalCard: forUser(bbd.physicalCard, user),
    cardSpend: forUser(bbd.cardSpend, user),
    gold: forUser(bbd.gold, user),
    vouchers: forUser(bbd.vouchers, user),
    bills: forUser(bbd.bills, user),
  };
}

/* The page model rendered straight away, before topFive has answered: the mock
   as is (the same object the live response is merged over), minus the end date
   so the banner keeps its "--" until the real countdown is known. */
export const placeholderBbd = () => ({ ...mapBbd(BBD_MOCK_RESPONSE), endsOn: null });

/* Resolves the campaign into a phase the page can render:
     { phase: 'live',   model } — campaign is enabled, full campaign model
     { phase: 'hidden' }        — campaign is disabled, nothing to show
   Visibility is driven solely by the top-level `enabled` flag; the campaign
   start / end dates are NOT used to decide whether to show the campaign.
   A genuine network / auth failure throws, so the page can offer a retry. */
export async function fetchBbd() {
  const res = await APIHelper.topFive();
  if (!res || res.error) throw new Error('BBD topFive request failed');

  const data = (res.data ?? res) || {};
  const liveBbd = data.bigBillionDays;
  const mockBbd = BBD_MOCK_RESPONSE.bigBillionDays;

  /* Only the `enabled` flag gates the campaign. Live value wins; the mock is
     used as a fallback while the backend hasn't shipped the section yet. */
  const enabled = liveBbd
    ? liveBbd.enabled !== false
    : (USE_MOCK ? mockBbd.enabled !== false : false);

  if (!enabled) return { phase: 'hidden' };

  /* campaign enabled → render the full campaign */
  let model;
  if (USE_MOCK) {
    /* live section data (incl. per-section `enabled` flags) wins over the mock,
       while the mock fills in any section the backend hasn't shipped yet. */
    const mergedBbd = { ...mergeBbd(mockBbd, liveBbd), enabled: true };
    model = mapBbd({ ...data, bigBillionDays: mergedBbd });
  } else {
    model = mapBbd(res);
  }
  return model ? { phase: 'live', model } : { phase: 'hidden' };
}

/* Claims the ₹10k card-spend reward. Resolves to the raw API response so the
   caller can inspect `error` (a `{ error: true }` payload, or a genuine
   network/auth failure surfaced as null) and react accordingly. */
export async function claimCardSpend() {
  const res = await APIHelper.fkSpendClaim();
  return res || null;
}
